/**
 * AlertSimulationPage — SuperAdmin-only page for launching real-pipeline
 * alert simulations (Session 2026-04-18 Q2: SuperAdmin only, Q3: duration ∈
 * {5, 15, 30, 60} minutes).
 *
 * Route visibility is controlled at the Sidebar level (roles: ['system_admin']).
 * The page itself ALSO gates rendering defensively so a direct-URL access
 * by a non-SuperAdmin shows a friendly "permission required" state instead
 * of a 403 page on form submit.
 *
 * 014-alerts / US7 / T136.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthProvider';
import { useStartSimulation } from '../hooks/useAdminAlerts';
import { SimulationConfirmationModal } from '../components/SimulationConfirmationModal';
import { RegionPicker } from '../components/RegionPicker';
import type { StartSimulationRequest } from '../api/adminAlertsApi';

const DURATIONS: Array<5 | 15 | 30 | 60> = [5, 15, 30, 60];
const ALERT_TYPES = ['AIR', 'ARTILLERY', 'URBAN_FIGHTS', 'CHEMICAL', 'NUCLEAR'];

export function AlertSimulationPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { admin } = useAuth();
  const startMutation = useStartSimulation();

  const [regionId, setRegionId] = useState('');
  const [regionName, setRegionName] = useState('');
  const [alertType, setAlertType] = useState('AIR');
  const [durationMinutes, setDurationMinutes] = useState<5 | 15 | 30 | 60>(15);
  const [modalOpen, setModalOpen] = useState(false);

  const canSimulate = useMemo(() => admin?.role === 'system_admin', [admin]);
  const isFormValid = regionId.trim().length > 0 && regionName.trim().length > 0;

  if (!canSimulate) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error">
          <AlertIcon />
          {t(
            'admin.alerts.simulate.superAdminOnly',
            'Only SuperAdmins can trigger alert simulations.',
          )}
        </Alert>
      </Box>
    );
  }

  const handleConfirm = () => {
    const request: StartSimulationRequest = { regionId, alertType, durationMinutes };
    startMutation.mutate(request, {
      onSuccess: (response) => {
        setModalOpen(false);
        toast({
          status: 'success',
          title: t('admin.alerts.simulate.startedTitle', 'Simulation started'),
          description: t(
            'admin.alerts.simulate.startedDetail',
            'Affecting {{count}} location(s); auto-ends at {{time}}.',
            {
              count: response.affectedLocationCount,
              time: new Date(response.expiresAt).toLocaleTimeString(),
            },
          ),
          duration: 6000,
        });
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : t('admin.alerts.simulate.genericError', 'Failed to start simulation.');
        toast({
          status: 'error',
          title: t('admin.alerts.simulate.startFailedTitle', "Couldn't start simulation"),
          description: message,
          duration: 8000,
        });
      },
    });
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="admin-alerts-simulate">
      <Stack spacing={4} maxW="640px">
        <Text color="gray.600">
          {t(
            'admin.alerts.simulate.description',
            'Trigger a real-pipeline simulation for the selected region. Every enabled customer location in the region (or any of its sub-districts / communities) will play its alarm-start audio; customer apps will show a "Test alert" badge. The simulation is rejected if no location is configured for the picked region — nothing would be written to history in that case.',
          )}
        </Text>

        <Box borderWidth="1px" borderRadius="md" p={4} bg="white">
        <Stack spacing={4}>
          <FormControl>
            <FormLabel>{t('admin.alerts.simulate.regionLabel', 'Region')}</FormLabel>
            <RegionPicker
              value={regionId}
              onChange={(id, region) => {
                setRegionId(id);
                setRegionName(region?.regionName ?? '');
              }}
              clearable={false}
              size="md"
              placeholder={t('admin.alerts.simulate.regionPlaceholder', 'Select a region…')}
              data-testid="simulate-region-picker"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t(
                'admin.alerts.simulate.regionHint',
                'The region name shown here becomes the type-to-confirm string in the next step.',
              )}
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel>{t('admin.alerts.simulate.alertTypeLabel', 'Alert type')}</FormLabel>
            <Select value={alertType} onChange={(e) => setAlertType(e.target.value)} data-testid="simulate-alert-type">
              {ALERT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>{t('admin.alerts.simulate.durationLabel', 'Duration')}</FormLabel>
            <RadioGroup
              value={String(durationMinutes)}
              onChange={(v) => setDurationMinutes(Number(v) as 5 | 15 | 30 | 60)}
            >
              <Stack direction="row" spacing={5} flexWrap="wrap">
                {DURATIONS.map((d) => (
                  <Radio key={d} value={String(d)} data-testid={`simulate-duration-${d}`}>
                    {t('admin.alerts.simulate.durationOption', '{{count}} min', { count: d })}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          </FormControl>

          <Button
            colorScheme="orange"
            isDisabled={!isFormValid}
            onClick={() => setModalOpen(true)}
            data-testid="simulate-open-confirmation"
            alignSelf="flex-start"
          >
            {t('admin.alerts.simulate.openConfirmationButton', 'Start simulation…')}
          </Button>
        </Stack>
      </Box>

        <SimulationConfirmationModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirm}
          regionName={regionName}
          alertType={alertType}
          durationMinutes={durationMinutes}
          isSubmitting={startMutation.isPending}
        />
      </Stack>
    </Box>
  );
}
